import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { Handlers } from "./types";

/**
 * UniversalEntityPageHandlersContext
 *
 * Contains all event handlers:
 * - CRUD handlers (create, edit, delete, view)
 * - Lifecycle handlers (itemCreated, itemUpdated, itemDeleted)
 * - Import/Export handlers
 * - Modal handlers (compose, detail)
 *
 * Part of the STABLE half: every member is a `useCallback`, so the value only
 * changes when one of those callbacks is genuinely rebuilt.
 */
export type UniversalEntityPageHandlersContextValue<T extends BaseEntity> = Handlers<T>;

// Entity type erased at the storage boundary; see UniversalEntityPageUIContext.
const UniversalEntityPageHandlersContext = createContext<
  UniversalEntityPageHandlersContextValue<BaseEntity> | undefined
>(undefined);

export function UniversalEntityPageHandlersProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageHandlersContextValue<T>;
}) {
  return (
    <UniversalEntityPageHandlersContext.Provider
      value={value as unknown as UniversalEntityPageHandlersContextValue<BaseEntity>}
    >
      {children}
    </UniversalEntityPageHandlersContext.Provider>
  );
}

/** Non-throwing read — `null` outside a provider. */
export function useOptionalUniversalEntityPageHandlersContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageHandlersContextValue<T> | null {
  const context = useContext(UniversalEntityPageHandlersContext);
  return (context as unknown as UniversalEntityPageHandlersContextValue<T> | undefined) ?? null;
}

export function useUniversalEntityPageHandlersContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageHandlersContextValue<T> {
  const context = useOptionalUniversalEntityPageHandlersContext<T>();
  if (!context) {
    throw new Error(
      "useUniversalEntityPageHandlersContext must be used within UniversalEntityPageHandlersProvider"
    );
  }
  return context;
}
